// src/components/Auth/Login.js
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Lock, LogIn, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuthApi } from "../../hooks/useAuthApi";
import { loginDefaultValues, loginSchema } from "../../utils/validationSchemas";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";

// Function to decode JWT token
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

const Login = ({ onToggle, loading, setLoading }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login: authLogin } = useAuth();
  const {
    login: loginUser,
    loading: apiLoading,
    error: apiError,
  } = useAuthApi();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: loginDefaultValues,
  });

  // Clear API errors when form changes
  React.useEffect(() => {
    if (apiError) {
      clearErrors("root");
    }
  }, [apiError, clearErrors]);

  const onSubmit = async (formData) => {
    setLoading(true);

    try {
      // Prepare data for API
      const apiData = {
        email: formData.email,
        password: formData.password,
      };

      const response = await loginUser(apiData);

      console.log("Login response:", response); // Debug log

      // Check for success based on your API response structure
      if (
        response?.status === 200 ||
        (response?.data && response.data.access_token)
      ) {
        const accessToken = response.data.access_token;

        // Store the access token in localStorage
        localStorage.setItem("access_token", accessToken);
        localStorage.setItem(
          "token_type",
          response.data.token_type || "bearer"
        );

        // Decode JWT token to get user information
        const decodedToken = decodeJWT(accessToken);

        if (decodedToken) {
          console.log("Decoded JWT Token:", decodedToken);

          // Extract user information from token
          const userFirstName = decodedToken.first_name || "N/A";
          const userId = decodedToken.sub || "N/A";
          const userRoles = decodedToken.roles || [];
          const userPhone = decodedToken.phone_number || "";

          console.log("User First Name from JWT:", userFirstName);
          console.log("User ID from JWT:", userId);
          console.log("User Roles from JWT:", userRoles);

          // Store user information in localStorage for easy access
          localStorage.setItem("user_first_name", userFirstName);
          localStorage.setItem("user_id", userId);
          localStorage.setItem("user_roles", JSON.stringify(userRoles));

          // Use first_name as username throughout the app
          localStorage.setItem("user_name", userFirstName);

          // Create user data object for AuthContext
          const userData = {
            id: userId,
            first_name: userFirstName,
            phone_number: userPhone,
            roles: userRoles,
            email: formData.email,
          };

          // Update AuthContext with user data
          authLogin(userData);

          // Log all available claims from the token
          console.log("All JWT claims:", Object.keys(decodedToken));
        } else {
          console.warn("Could not decode JWT token");
        }

        // Show success toast with user information
        toast({
          title: "Login successful!",
          description: response.data?.message || "Welcome back to GEETH365.",
          duration: 3000,
        });

        console.log("Login toast should have been shown"); // Debug log

        // Navigate to home page after a short delay
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        // Handle unexpected response
        setError("root", {
          type: "server",
          message: "Login completed but unexpected response.",
        });
      }
    } catch (error) {
      console.error("Login error:", error); // Debug log

      // Handle specific error cases - REVERTED TO ORIGINAL LOGIC
      if (error.response?.data?.errors) {
        // Handle field-specific errors from API
        const fieldErrors = error.response.data.errors;
        Object.keys(fieldErrors).forEach((field) => {
          setError(field, {
            type: "server",
            message: fieldErrors[field][0],
          });
        });
      } else if (error.response?.data?.detail) {
        // Handle common authentication errors
        const errorDetail = error.response.data.detail;
        setError("root", {
          type: "server",
          message: errorDetail,
        });
      } else if (error.response?.data?.message) {
        setError("root", {
          type: "server",
          message: error.response.data.message,
        });
      } else {
        setError("root", {
          type: "server",
          message: "Login failed. Please check your credentials and try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || apiLoading;

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="login-email" className="flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Email</span>
          </Label>
          <Input
            id="login-email"
            type="email"
            placeholder="Enter your email"
            className="mt-1"
            {...register("email")}
            disabled={isLoading}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <Label
            htmlFor="login-password"
            className="flex items-center space-x-2"
          >
            <Lock className="w-4 h-4" />
            <span>Password</span>
          </Label>
          <Input
            id="login-password"
            type="password"
            placeholder="Enter your password"
            className="mt-1"
            {...register("password")}
            disabled={isLoading}
          />
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">
              {errors.password.message}
            </p>
          )}
        </div>

        {(errors.root || apiError) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">
              {errors.root?.message || apiError}
            </p>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2.5"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Logging in...
            </>
          ) : (
            <>
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </>
          )}
        </Button>
      </form>

      <div className="text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?{" "}
          <button
            type="button"
            onClick={() => onToggle("signup")}
            className="text-orange-600 hover:text-orange-700 font-medium"
            disabled={isLoading}
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
};

export default Login;
